/**
 * FieldFormDrawer — create/edit sheet for a single attribute field (PIM Wave
 * P1 Task 3, design notes Surface 2). `BottomSheet` (desktop mode per
 * `useIsDesktop`, ProductDrawer precedent). Two modes, prop-driven by
 * `field: AttributeField | null` (`null` = create).
 *
 * CREATE mode: a type picker (7 radio-cards) gates the rest of the form —
 * `field_type` is part of the create payload and becomes immutable the
 * moment the field exists (`FieldUpdate` has no `field_type` key at all).
 * Once chosen, `slug` auto-derives from the RU name (`../lib/slug`) until
 * the admin edits it manually (`slugTouched`); `slug` is likewise absent
 * from `FieldUpdate` — immutable after create.
 *
 * EDIT mode: type renders as a static badge (no control); slug renders as
 * muted mono text + a copy-to-clipboard button. PATCH sends only the keys
 * that actually changed vs. the loaded field (`buildUpdateBody`) — never
 * `slug`/`field_type` (backend `extra="forbid"` would 422 on either).
 *
 * `select`-type `OptionsEditor` mounts here in edit mode only — options nest
 * under a field id, so a create-mode select field shows a "save the field
 * first" hint instead (Task 4, brief §1/§5). The "manage groups" affordance
 * renders `GroupManager` as an INLINE collapsible section (not a nested
 * `BottomSheet` — see `GroupManager.tsx`'s stacking note for why).
 *
 * CREATE-mode select-field transition (brief §5): a successful create POST
 * for a `select` field does NOT close the drawer. `createdField` holds the
 * server response and `effectiveField` (`field ?? createdField`) drives
 * render — the drawer re-renders in edit mode on that field, an
 * informational toast fires, and the options section scrolls into view +
 * autofocuses its add-option input (`autoFocusOptions`). Every other
 * create (non-select) keeps the original close-on-success behavior.
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { Controller, useForm, type UseFormRegisterReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronDown, Copy } from "lucide-react";
import { z } from "zod";

import { BottomSheet, Button, Field, GPill } from "@garden/ui";
import { TrilingualInput, type TrilingualValue } from "@/features/admin/_shared";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ApiError } from "@/lib/api-client";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import {
  FIELD_TYPE_VALUES,
  UNIT_KIND_VALUES,
  type AttributeField,
  type AttributeFieldCreate,
  type AttributeFieldUpdate,
  type FieldTypeValue,
} from "@/features/admin/attribute-fields/api";
import {
  useAttributeFieldGroups,
  useCreateAttributeField,
  useUpdateAttributeField,
} from "@/features/admin/attribute-fields/hooks";
import {
  FIELD_SLUG_MAX_LEN,
  FIELD_SLUG_PATTERN,
  slugify,
} from "@/features/admin/attribute-fields/lib/slug";
import {
  FIELD_TYPE_META,
  allowedUnitsForKind,
  fieldTypeMeta,
} from "@/features/admin/attribute-fields/lib/fieldTypeMeta";
import { OptionsEditor } from "@/features/admin/attribute-fields/components/OptionsEditor";
import { GroupManager } from "@/features/admin/attribute-fields/components/GroupManager";

const SELECT_CLASS =
  "min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

// ---------------------------------------------------------------------
// Form values + Zod schema
// ---------------------------------------------------------------------

const trilingualSchema = z.object({ ru: z.string(), ky: z.string(), en: z.string() });

/**
 * `fieldType` drives the size-coherence branch only (the object SHAPE is
 * identical across all 7 types — only which superRefine branches fire
 * differs), so one schema factory covers create + edit for every type. The
 * return type is left to inference (not annotated as `z.ZodType<...>`) —
 * zodResolver needs the concrete `ZodEffects<ZodObject<...>>` shape;
 * widening it to the abstract `ZodType` base breaks the resolver's overload
 * resolution.
 */
function buildFieldSchema(fieldType: FieldTypeValue) {
  return z
    .object({
      slug: z
        .string()
        .min(1, "formError.required")
        .max(FIELD_SLUG_MAX_LEN, "pim.fields.form.formError.slugPattern")
        .regex(FIELD_SLUG_PATTERN, "pim.fields.form.formError.slugPattern"),
      name: trilingualSchema,
      hint: trilingualSchema,
      example: trilingualSchema,
      group_id: z.string(),
      unit_kind: z.string(),
      canonical_unit: z.string(),
      matching_weight: z.string(),
    })
    .superRefine((data, ctx) => {
      if (data.name.ru.trim() === "") {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "formError.required", path: ["name", "ru"] });
      }
      const hintHasAny = [data.hint.ru, data.hint.ky, data.hint.en].some((v) => v.trim() !== "");
      if (hintHasAny && data.hint.ru.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "pim.fields.form.formError.ruRequiredForHint",
          path: ["hint", "ru"],
        });
      }
      const exampleHasAny = [data.example.ru, data.example.ky, data.example.en].some(
        (v) => v.trim() !== "",
      );
      if (exampleHasAny && data.example.ru.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "pim.fields.form.formError.ruRequiredForExample",
          path: ["example", "ru"],
        });
      }
      const weightRaw = data.matching_weight.trim();
      if (weightRaw !== "") {
        const n = Number(weightRaw.replace(",", "."));
        if (!Number.isFinite(n) || n < 0) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "formError.numeric", path: ["matching_weight"] });
        }
      }
      if (fieldType === "size") {
        if (data.unit_kind.trim() === "") {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "formError.required", path: ["unit_kind"] });
        }
        if (data.canonical_unit.trim() === "") {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "formError.required", path: ["canonical_unit"] });
        }
      }
    });
}

type FieldFormValues = z.infer<ReturnType<typeof buildFieldSchema>>;

function toTrilingual(ru: string, ky: string | null, en: string | null): TrilingualValue {
  return { ru, ky: ky ?? "", en: en ?? "" };
}

/**
 * `prefillName` (PIM Wave P3 Task 4) seeds the RU name in CREATE mode only —
 * the best-effort handoff from an approved catalog suggestion (see
 * `FieldFormDrawerProps.createPrefill` below). Ignored in edit mode.
 */
function defaultFormValues(field: AttributeField | null, prefillName?: string): FieldFormValues {
  if (!field) {
    return {
      slug: "",
      name: { ru: prefillName ?? "", ky: "", en: "" },
      hint: { ru: "", ky: "", en: "" },
      example: { ru: "", ky: "", en: "" },
      group_id: "",
      unit_kind: "",
      canonical_unit: "",
      matching_weight: "0",
    };
  }
  return {
    slug: field.slug,
    name: toTrilingual(field.name_ru, field.name_ky, field.name_en),
    hint: toTrilingual(field.hint_ru ?? "", field.hint_ky, field.hint_en),
    example: toTrilingual(field.example_ru ?? "", field.example_ky, field.example_en),
    group_id: field.group_id ?? "",
    unit_kind: field.unit_kind ?? "",
    canonical_unit: field.canonical_unit ?? "",
    matching_weight: field.matching_weight,
  };
}

interface FieldWriteValues {
  name_ru: string;
  name_ky: string | null;
  name_en: string | null;
  hint_ru: string | null;
  hint_ky: string | null;
  hint_en: string | null;
  example_ru: string | null;
  example_ky: string | null;
  example_en: string | null;
  group_id: string | null;
  unit_kind: string | null;
  canonical_unit: string | null;
  matching_weight: string;
}

/** PATCH sends ONLY changed keys — never `slug`/`field_type` (immutable). */
function buildUpdateBody(original: AttributeField, next: FieldWriteValues): AttributeFieldUpdate {
  const body: AttributeFieldUpdate = {};
  if (next.name_ru !== original.name_ru) body.name_ru = next.name_ru;
  if (next.name_ky !== original.name_ky) body.name_ky = next.name_ky;
  if (next.name_en !== original.name_en) body.name_en = next.name_en;
  if (next.hint_ru !== original.hint_ru) body.hint_ru = next.hint_ru;
  if (next.hint_ky !== original.hint_ky) body.hint_ky = next.hint_ky;
  if (next.hint_en !== original.hint_en) body.hint_en = next.hint_en;
  if (next.example_ru !== original.example_ru) body.example_ru = next.example_ru;
  if (next.example_ky !== original.example_ky) body.example_ky = next.example_ky;
  if (next.example_en !== original.example_en) body.example_en = next.example_en;
  if (next.group_id !== original.group_id) body.group_id = next.group_id;
  if (next.unit_kind !== original.unit_kind) body.unit_kind = next.unit_kind;
  if (next.canonical_unit !== original.canonical_unit) body.canonical_unit = next.canonical_unit;
  if (next.matching_weight !== original.matching_weight) body.matching_weight = next.matching_weight;
  return body;
}

/**
 * Maps the size-coherence server codes onto the fieldset, per brief §10. The
 * admin field-PATCH path (`apps/api/garden/features/catalog_schema/
 * service.py`, `update_field`) actually raises `catalog_schema.field.
 * unit_kind_locked` (422 — `FieldUnitKindLockedError`, a size field's
 * `unit_kind` is locked once ANY `product_attribute_values` row references
 * it) and `catalog_schema.field.size_coherence_invalid` (422 —
 * `FieldSizeCoherenceError`, the prospective `unit_kind`/`canonical_unit`
 * pair is incoherent for the field's `field_type`) — P1 Task 3 review Finding
 * 1: those two codes were verified against `service.py`, not the `pim.unit_*`
 * codes this mapping originally matched only. The `pim.unit_*` matches stay
 * wired as a defensive fallback (kept from the original mapping) in case a
 * future caller of this same handler reaches them via a different code path.
 * Any other `ApiError` falls back to a toast (form-level alert), matching the
 * `ProductEditForm`/`CategoryFormDialog` precedent.
 */
function mapUnitCoherenceError(
  err: ApiError,
): { field: "unit_kind" | "canonical_unit"; messageKey: string } | null {
  if (err.code === "pim.unit_unknown" || err.code === "pim.unit_not_allowed") {
    return { field: "canonical_unit", messageKey: "pim.fields.form.formError.unitLocked" };
  }
  if (err.code === "catalog_schema.field.unit_kind_locked") {
    return { field: "unit_kind", messageKey: "pim.fields.form.formError.unitLocked" };
  }
  if (err.code === "catalog_schema.field.size_coherence_invalid") {
    return {
      field: "canonical_unit",
      messageKey: "pim.fields.form.formError.sizeCoherenceInvalid",
    };
  }
  return null;
}

// ---------------------------------------------------------------------
// Type picker (create mode, step 1)
// ---------------------------------------------------------------------

function TypePicker({ onSelect }: { onSelect: (type: FieldTypeValue) => void }): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-[var(--g-secondary)]">{t("pim.fields.form.typePickerHint")}</p>
      <div
        role="radiogroup"
        aria-label={t("pim.fields.form.typePickerLabel")}
        className="grid grid-cols-2 gap-2"
      >
        {FIELD_TYPE_META.map((meta) => {
          const MetaIcon = meta.icon;
          return (
            <button
              key={meta.value}
              type="button"
              role="radio"
              aria-checked={false}
              data-testid={`pim-field-type-${meta.value}`}
              onClick={() => onSelect(meta.value)}
              className="flex min-h-[44px] flex-col items-start gap-1.5 rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-3 text-left transition-colors hover:border-[var(--g-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
            >
              <MetaIcon size={20} strokeWidth={1.75} aria-hidden className="text-[var(--g-primary)]" />
              <span className="text-sm font-semibold text-foreground">{t(meta.labelKey)}</span>
              <span className="text-xs text-[var(--g-secondary)]">{t(meta.descriptionKey)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Create/edit form (step 2 / edit)
// ---------------------------------------------------------------------

interface FieldFormProps {
  mode: "create" | "edit";
  fieldType: FieldTypeValue;
  field: AttributeField | null;
  onCancelType?: () => void;
  onDone: () => void;
  /** Create-mode success for a `select` field transitions in place instead
   * of closing (brief §5) — never called for other types/modes. */
  onSelectFieldCreated: (created: AttributeField) => void;
  /** True for exactly one render: the create -> edit transition right after
   * a `select` field's create POST succeeds. Drives the options-section
   * scroll + autofocus (brief §5). */
  justTransitionedToOptions: boolean;
  /** PIM Wave P3 Task 4 handoff — seeds the RU name once, create mode only. */
  prefillName?: string;
}

function FieldForm({
  mode,
  fieldType,
  field,
  onCancelType,
  onDone,
  onSelectFieldCreated,
  justTransitionedToOptions,
  prefillName,
}: FieldFormProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const { data: groupsData } = useAttributeFieldGroups();
  const groups = groupsData?.groups ?? [];
  const createMutation = useCreateAttributeField();
  const updateMutation = useUpdateAttributeField();
  const isPending = createMutation.isPending || updateMutation.isPending;
  const [slugTouched, setSlugTouched] = useState(mode === "edit");
  const [groupManagerExpanded, setGroupManagerExpanded] = useState(false);
  const optionsSectionRef = useRef<HTMLDivElement | null>(null);

  // Scrolls the options section into view once, right after a create-mode
  // select field just transitioned to edit mode (brief §5 "scrolls/focuses
  // the options section"). This component remounts (new `key`) on that
  // transition, so an empty-deps mount effect fires exactly once for it.
  useEffect(() => {
    if (justTransitionedToOptions) {
      optionsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const schema = useMemo(() => buildFieldSchema(fieldType), [fieldType]);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<FieldFormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultFormValues(field, prefillName),
  });

  // `name` is written wholesale via the `Controller` below (`TrilingualInput`
  // always calls `onChange` with the full `{ru, ky, en}` triple), so RHF
  // signals the change at the OBJECT path "name", never at the leaf path
  // "name.ru". `watch("name.ru")` subscribes to that leaf and is never
  // notified by an object-level signal (RHF's watch-match only fires a
  // subscriber when the changed path equals or is a PREFIX of the watched
  // path, not the reverse) — it would silently stay "" forever, which is
  // exactly the live bug (slug never auto-derives from the RU name).
  // Watching the object itself and reading `.ru` off it matches the actual
  // signaled path, so it reacts correctly.
  const nameRu = watch("name").ru;
  const unitKind = watch("unit_kind");

  useEffect(() => {
    if (mode !== "create" || slugTouched) return;
    setValue("slug", slugify(nameRu ?? ""), { shouldValidate: true, shouldDirty: true });
  }, [mode, nameRu, slugTouched, setValue]);

  const errText = (key?: string): string | undefined => (key ? t(key) : undefined);

  const slugRegister: UseFormRegisterReturn<"slug"> = register("slug");
  const unitKindRegister: UseFormRegisterReturn<"unit_kind"> = register("unit_kind");

  const submit = (values: FieldFormValues): void => {
    const next: FieldWriteValues = {
      name_ru: values.name.ru.trim(),
      name_ky: values.name.ky.trim() || null,
      name_en: values.name.en.trim() || null,
      hint_ru: values.hint.ru.trim() || null,
      hint_ky: values.hint.ky.trim() || null,
      hint_en: values.hint.en.trim() || null,
      example_ru: values.example.ru.trim() || null,
      example_ky: values.example.ky.trim() || null,
      example_en: values.example.en.trim() || null,
      group_id: values.group_id || null,
      unit_kind: fieldType === "size" ? values.unit_kind || null : null,
      canonical_unit: fieldType === "size" ? values.canonical_unit || null : null,
      matching_weight: values.matching_weight.trim() === "" ? "0" : values.matching_weight.trim(),
    };

    const onWriteError = (err: unknown): void => {
      if (err instanceof ApiError) {
        const mapped = mapUnitCoherenceError(err);
        if (mapped) {
          setError(mapped.field, { type: "server", message: mapped.messageKey });
          return;
        }
      }
      toast.error(t("error.generic"));
    };

    if (mode === "create") {
      const body: AttributeFieldCreate = {
        slug: values.slug,
        field_type: fieldType,
        ...next,
      };
      createMutation.mutate(body, {
        onSuccess: (created) => {
          if (fieldType === "select") {
            // Brief §5 — do NOT close; transition to edit mode on the
            // returned field so OptionsEditor can mount immediately.
            toast.success(t("pim.fields.form.createdAddOptions"));
            onSelectFieldCreated(created);
          } else {
            toast.success(t("pim.fields.form.createSuccess"));
            onDone();
          }
        },
        onError: onWriteError,
      });
    } else if (field) {
      const body = buildUpdateBody(field, next);
      updateMutation.mutate(
        { id: field.id, body },
        {
          onSuccess: () => {
            toast.success(t("pim.fields.form.updateSuccess"));
            onDone();
          },
          onError: onWriteError,
        },
      );
    }
  };

  const copySlug = async (): Promise<void> => {
    if (!field) return;
    try {
      await navigator.clipboard.writeText(field.slug);
      toast.success(t("pim.fields.form.slugCopied"));
    } catch {
      toast.error(t("error.generic"));
    }
  };

  const meta = fieldTypeMeta(fieldType);
  const MetaIcon = meta?.icon;
  const canonicalOptions = allowedUnitsForKind(unitKind);

  return (
    <form data-testid="pim-field-form" onSubmit={handleSubmit(submit)} className="flex flex-col gap-5">
      {/* --- Identity ------------------------------------------------ */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {MetaIcon ? (
              <MetaIcon size={18} strokeWidth={1.75} aria-hidden className="text-[var(--g-primary)]" />
            ) : null}
            <GPill tone="primary">
              {meta ? t(meta.labelKey) : fieldType}
            </GPill>
          </div>
          {onCancelType ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onCancelType}
              className="min-h-[44px]"
            >
              {t("pim.fields.form.changeType")}
            </Button>
          ) : null}
        </div>

        <Controller
          control={control}
          name="name"
          render={({ field: nameField }) => (
            <TrilingualInput
              id="pim-field-form-name"
              label={t("pim.fields.form.name")}
              value={nameField.value}
              onChange={nameField.onChange}
              required
              error={errText(errors.name?.ru?.message)}
            />
          )}
        />

        {mode === "create" ? (
          <Field
            label={t("pim.fields.form.slug")}
            data-testid="pim-field-form-slug"
            hint={t("pim.fields.form.slugHint")}
            error={errText(errors.slug?.message)}
            {...slugRegister}
            onChange={(event) => {
              void slugRegister.onChange(event);
              setSlugTouched(true);
            }}
          />
        ) : (
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold text-foreground">{t("pim.fields.form.slug")}</span>
            <div className="flex items-center gap-2">
              <span
                data-testid="pim-field-form-slug-static"
                className="rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-elevated)] px-3 py-2 font-mono text-sm text-[var(--g-secondary)]"
              >
                {field?.slug}
              </span>
              <button
                type="button"
                data-testid="pim-field-form-slug-copy"
                onClick={() => void copySlug()}
                aria-label={t("pim.fields.form.slugCopy")}
                className="grid h-9 w-9 place-items-center rounded-full text-[var(--g-secondary)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
              >
                <Copy size={16} strokeWidth={1.75} aria-hidden />
              </button>
            </div>
          </div>
        )}
      </section>

      {/* --- Guidance -------------------------------------------------- */}
      <section className="flex flex-col gap-3">
        <Controller
          control={control}
          name="hint"
          render={({ field: hintField }) => (
            <TrilingualInput
              id="pim-field-form-hint"
              label={t("pim.fields.form.hint")}
              value={hintField.value}
              onChange={hintField.onChange}
              multiline
              error={errText(errors.hint?.ru?.message)}
            />
          )}
        />
        <Controller
          control={control}
          name="example"
          render={({ field: exampleField }) => (
            <TrilingualInput
              id="pim-field-form-example"
              label={t("pim.fields.form.example")}
              value={exampleField.value}
              onChange={exampleField.onChange}
              multiline
              error={errText(errors.example?.ru?.message)}
            />
          )}
        />
      </section>

      {/* --- Type-specific ---------------------------------------------- */}
      {fieldType === "size" ? (
        <section className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
            {t("pim.fields.form.sizeSection")}
          </h3>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="pim-field-form-unit-kind" className="text-[13px] font-semibold text-foreground">
              {t("pim.fields.form.unitKind")}
            </label>
            <select
              id="pim-field-form-unit-kind"
              data-testid="pim-field-form-unit-kind"
              className={SELECT_CLASS}
              {...unitKindRegister}
              onChange={(event) => {
                void unitKindRegister.onChange(event);
                setValue("canonical_unit", "");
              }}
            >
              <option value="">{t("pim.fields.form.unitKindPlaceholder")}</option>
              {UNIT_KIND_VALUES.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`pim.fields.form.unitKindOption.${kind}`)}
                </option>
              ))}
            </select>
            {errors.unit_kind?.message ? (
              <p role="alert" className="text-xs text-[var(--g-error)]">
                {errText(errors.unit_kind.message)}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="pim-field-form-canonical-unit"
              className="text-[13px] font-semibold text-foreground"
            >
              {t("pim.fields.form.canonicalUnit")}
            </label>
            <select
              id="pim-field-form-canonical-unit"
              data-testid="pim-field-form-canonical-unit"
              className={SELECT_CLASS}
              disabled={!unitKind}
              {...register("canonical_unit")}
            >
              <option value="">{t("pim.fields.form.canonicalUnitPlaceholder")}</option>
              {canonicalOptions.map((unit) => (
                <option key={unit} value={unit}>
                  {t(`pim.fields.form.unitOption.${unit}`)}
                </option>
              ))}
            </select>
            {errors.canonical_unit?.message ? (
              <p role="alert" className="text-xs text-[var(--g-error)]">
                {errText(errors.canonical_unit.message)}
              </p>
            ) : null}
          </div>
        </section>
      ) : fieldType === "select" ? (
        <section ref={optionsSectionRef} className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
            {t("pim.fields.form.optionsSection")}
          </h3>
          {mode === "edit" && field ? (
            <OptionsEditor fieldId={field.id} autoFocusAdd={justTransitionedToOptions} />
          ) : (
            <p className="text-sm text-[var(--g-secondary)]">{t("pim.options.saveFieldFirst")}</p>
          )}
        </section>
      ) : null}

      {/* --- Organization ------------------------------------------------ */}
      <section className="flex flex-col gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
          {t("pim.fields.form.organizationSection")}
        </h3>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="pim-field-form-group" className="text-[13px] font-semibold text-foreground">
            {t("pim.fields.form.group")}
          </label>
          <select
            id="pim-field-form-group"
            data-testid="pim-field-form-group"
            className={SELECT_CLASS}
            {...register("group_id")}
          >
            <option value="">{t("pim.fields.form.groupNone")}</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name_ru}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-testid="pim-field-form-manage-groups-toggle"
            aria-expanded={groupManagerExpanded}
            onClick={() => setGroupManagerExpanded((v) => !v)}
            className="flex min-h-[44px] items-center gap-1.5 self-start text-sm font-medium text-[var(--g-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
          >
            <ChevronDown
              size={16}
              strokeWidth={1.75}
              aria-hidden
              className={groupManagerExpanded ? "rotate-180 transition-transform" : "transition-transform"}
            />
            {t("pim.groups.manageToggle")}
          </button>
          {/* Inline, not a nested BottomSheet — see GroupManager.tsx's
              stacking note (brief §7): a second BottomSheet nested inside
              this drawer's own open BottomSheet would have its `fixed`
              positioning trapped by the outer panel's Framer Motion
              transform. */}
          {groupManagerExpanded ? (
            <div className="rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-ground)] p-3">
              <GroupManager />
            </div>
          ) : null}
        </div>

        <Field
          label={t("pim.fields.form.matchingWeight")}
          data-testid="pim-field-form-matching-weight"
          type="number"
          min={0}
          step={0.1}
          inputMode="decimal"
          hint={t("pim.fields.form.matchingWeightHint")}
          error={errText(errors.matching_weight?.message)}
          {...register("matching_weight")}
        />
      </section>

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onDone}
          disabled={isPending}
          className="min-h-[44px]"
        >
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          data-testid="pim-field-form-submit"
          loading={isPending}
          className="min-h-[44px]"
        >
          {mode === "create" ? t("pim.fields.form.submitCreate") : t("pim.fields.form.submitUpdate")}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------
// Drawer shell
// ---------------------------------------------------------------------

export interface FieldFormDrawerProps {
  open: boolean;
  /** `null` = create mode; a loaded field = edit mode. */
  field: AttributeField | null;
  onClose: () => void;
  /**
   * PIM Wave P3 Task 4 best-effort handoff seed — a suggestion's proposed
   * name/type, applied in CREATE mode only (`field === null`). `field_type`
   * pre-selects the type-picker step when it is a recognized
   * {@link FieldTypeValue}; an unrecognized/absent type just leaves the
   * picker showing (the admin picks manually) while the name still seeds.
   * Applied exactly once per `open` transition — see the effect below.
   */
  createPrefill?: { name: string; field_type: string | null };
}

export function FieldFormDrawer({
  open,
  field,
  onClose,
  createPrefill,
}: FieldFormDrawerProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();
  const [pendingType, setPendingType] = useState<FieldTypeValue | null>(null);
  // Brief §5: a create-mode `select` field's successful POST transitions the
  // drawer to edit mode on the RETURNED field instead of closing. `field`
  // itself stays `null` (the parent list's `editingField` state is
  // untouched — this is a same-drawer-session-only transition), so this
  // local override drives `effectiveField` below.
  const [createdField, setCreatedField] = useState<AttributeField | null>(null);

  // Reset the create-mode type-picker step (and the local create->edit
  // transition) whenever the sheet closes, so the next "Create field" open
  // always starts at step 1 regardless of what was picked (or created)
  // last time. On the OPEN edge, in create mode, seed `pendingType` from a
  // PIM Wave P3 Task 4 suggestion handoff if one is present and its
  // `field_type` is a recognized value — this only re-fires when `open`
  // itself flips (not on every `createPrefill`/`field` identity change
  // while the sheet stays open), so it never clobbers an admin's own
  // type pick mid-session.
  useEffect(() => {
    if (!open) {
      setPendingType(null);
      setCreatedField(null);
      return;
    }
    if (field === null && createPrefill?.field_type) {
      const proposedType = createPrefill.field_type;
      if ((FIELD_TYPE_VALUES as readonly string[]).includes(proposedType)) {
        setPendingType(proposedType as FieldTypeValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const effectiveField = field ?? createdField;
  const mode: "create" | "edit" = effectiveField ? "edit" : "create";
  const activeType = (effectiveField?.field_type as FieldTypeValue | undefined) ?? pendingType;
  const title = effectiveField ? t("pim.fields.form.editTitle") : t("pim.fields.form.createTitle");

  return (
    <BottomSheet open={open} onClose={onClose} title={title} desktop={isDesktop}>
      {mode === "create" && activeType === null ? (
        <TypePicker onSelect={setPendingType} />
      ) : (
        <FieldForm
          key={effectiveField?.id ?? activeType ?? "new"}
          mode={mode}
          fieldType={activeType ?? "text"}
          field={effectiveField}
          onCancelType={mode === "create" ? () => setPendingType(null) : undefined}
          onDone={onClose}
          onSelectFieldCreated={setCreatedField}
          // `createdField` is only ever set from the create-mode branch, so
          // its presence alone means "just transitioned" for this session.
          justTransitionedToOptions={createdField !== null}
          prefillName={mode === "create" ? createPrefill?.name : undefined}
        />
      )}
    </BottomSheet>
  );
}

export default FieldFormDrawer;
